import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  CheckSquare,
} from 'lucide-react';
import { eventsApi, tasksApi, CalendarEvent, CalendarTask } from '@/lib/api';
import { cn } from '@/lib/utils';

// Unified calendar item type
type CalendarItem =
  | { type: 'event'; data: CalendarEvent }
  | { type: 'task'; data: CalendarTask };

interface ProjectCalendarProps {
  projectId: string;
  onAddEvent?: (date?: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onTaskClick?: (task: CalendarTask) => void;
  canEditEvent?: (event: CalendarEvent) => boolean;
  className?: string;
  weekDays?: 5 | 7; // 5 = Mon-Fri, 7 = Sun-Sat
  // Render nothing when the current week has no events or due tasks. Once
  // someone navigates to another week the card stays put.
  hideWhenEmpty?: boolean;
}

// Helper to check if two dates are the same day (local time)
function isSameDay(date1: Date, date2: Date) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// Helper to check if two dates are the same day (UTC) - for task due dates
function isSameDayUTC(date1: Date, date2: Date) {
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
}

// Helper to check if a date falls within a range
function isDateInRange(date: Date, start: Date, end: Date | null) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();

  if (!end) {
    return d === s;
  }

  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return d >= s && d <= e;
}

// Get start of week (Sunday by default)
function getStartOfWeek(date: Date, startOnMonday = false): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = startOnMonday ? (day === 0 ? -6 : 1 - day) : -day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Format date range for header
function formatWeekRange(startDate: Date, endDate: Date): string {
  const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const year = endDate.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}–${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
}

// Event color mapping
const eventColors: Record<string, { bg: string; border: string; text: string }> = {
  '#22c55e': { bg: 'bg-success/15', border: 'border-l-success', text: 'text-success' },
  '#f59e0b': { bg: 'bg-warning/15', border: 'border-l-warning', text: 'text-warning' },
  '#e61515': { bg: 'bg-primary/15', border: 'border-l-primary', text: 'text-primary' },
  '#8b5cf6': { bg: 'bg-purple-100', border: 'border-l-purple-500', text: 'text-purple-600' },
  '#06b6d4': { bg: 'bg-cyan-100', border: 'border-l-cyan-500', text: 'text-cyan-600' },
};

const defaultColor = { bg: 'bg-accent/15', border: 'border-l-accent', text: 'text-accent-700' };

// Task color (green with checkbox icon)
const taskColor = { bg: 'bg-success/15', border: 'border-l-success', text: 'text-success' };

function getEventColor(color?: string) {
  if (color && eventColors[color]) {
    return eventColors[color];
  }
  return defaultColor;
}

export function ProjectCalendar({
  projectId,
  onAddEvent,
  onEventClick,
  onTaskClick,
  canEditEvent,
  className,
  weekDays = 7,
  hideWhenEmpty = false,
}: ProjectCalendarProps) {
  const navigate = useNavigate();
  const today = new Date();
  const [weekStart, setWeekStart] = useState(() => getStartOfWeek(today, weekDays === 5));

  // Calculate the days to display
  const days = useMemo(() => {
    const result: Date[] = [];
    const numDays = weekDays === 5 ? 5 : 7;

    for (let i = 0; i < numDays; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      result.push(d);
    }
    return result;
  }, [weekStart, weekDays]);

  const weekEnd = days[days.length - 1];

  // Fetch events for the week
  const { data: events = [] } = useQuery({
    queryKey: ['events', projectId, weekStart.toISOString()],
    queryFn: () => {
      const end = new Date(weekEnd);
      end.setHours(23, 59, 59, 999);
      return eventsApi.getByProject(
        projectId,
        weekStart.toISOString(),
        end.toISOString()
      );
    },
    staleTime: 30000,
  });

  // Fetch tasks with due dates for the week
  const { data: tasks = [] } = useQuery({
    queryKey: ['calendar-tasks', projectId, weekStart.toISOString()],
    queryFn: () => {
      const end = new Date(weekEnd);
      end.setHours(23, 59, 59, 999);
      return tasksApi.getByDateRange(
        projectId,
        weekStart.toISOString(),
        end.toISOString()
      );
    },
    staleTime: 30000,
  });

  // Group events and tasks by day
  const itemsByDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    days.forEach((day) => {
      const key = day.toISOString().split('T')[0];
      const items: CalendarItem[] = [];

      // Add events for this day
      events.forEach((event) => {
        const start = new Date(event.startDate);
        const end = event.endDate ? new Date(event.endDate) : null;
        if (isDateInRange(day, start, end)) {
          items.push({ type: 'event', data: event });
        }
      });

      // Add tasks with due date on this day (compare in UTC to avoid timezone issues)
      tasks.forEach((task) => {
        const dueDate = new Date(task.dueDate);
        // Create a UTC date for the current day to compare with the UTC due date
        const dayUTC = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()));
        if (isSameDayUTC(dayUTC, dueDate)) {
          items.push({ type: 'task', data: task });
        }
      });

      map.set(key, items);
    });
    return map;
  }, [days, events, tasks]);

  // Check if there are any items this week
  const hasItemsThisWeek = useMemo(() => {
    return Array.from(itemsByDay.values()).some(items => items.length > 0);
  }, [itemsByDay]);

  const goToPreviousWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() - 7);
    setWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + 7);
    setWeekStart(newStart);
  };

  const goToToday = () => {
    setWeekStart(getStartOfWeek(new Date(), weekDays === 5));
  };

  const isCurrentWeek = isSameDay(getStartOfWeek(today, weekDays === 5), weekStart);

  // Also hidden while the week loads, so an empty card never flashes in
  if (hideWhenEmpty && isCurrentWeek && !hasItemsThisWeek) return null;

  const dayLabels = weekDays === 5
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={cn('bg-white rounded-xl border border-grey-200/50 shadow-card overflow-hidden', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 pb-4">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-accent" />
          <h2 className="text-lg font-semibold text-secondary">Schedule</h2>
        </div>
        {onAddEvent && (
          <button
            onClick={() => onAddEvent()}
            className="p-1.5 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-accent transition-colors"
            title="Add event"
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Week Navigation - always visible */}
      <div className="flex items-center justify-between px-6 py-2 bg-grey-50/50">
        <button
          onClick={goToPreviousWeek}
          className="p-1.5 text-grey-400 hover:text-secondary hover:bg-white rounded transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-secondary">
            {formatWeekRange(days[0], weekEnd)}
          </span>
          {!isCurrentWeek && (
            <button
              onClick={goToToday}
              className="text-xs text-accent hover:underline font-medium"
            >
              Today
            </button>
          )}
        </div>
        <button
          onClick={goToNextWeek}
          className="p-1.5 text-grey-400 hover:text-secondary hover:bg-white rounded transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Weekly Grid or Empty State */}
      {hasItemsThisWeek ? (
        <div className="divide-y divide-grey-100">
          {days.map((day, idx) => {
            const isToday = isSameDay(day, today);
            const dayKey = day.toISOString().split('T')[0];
            const dayItems = itemsByDay.get(dayKey) || [];
            const dayOfWeek = dayLabels[idx];
            const dayNum = day.getDate();

            return (
              <div
                key={dayKey}
                className={cn(
                  'flex min-h-[36px]',
                  isToday && 'bg-accent/5'
                )}
              >
                {/* Day label */}
                <div
                  onClick={() => onAddEvent?.(day)}
                  className={cn(
                    'w-12 shrink-0 flex flex-col items-center justify-center py-1.5 border-r border-grey-100',
                    onAddEvent && 'hover:bg-grey-50 cursor-pointer transition-colors'
                  )}
                >
                  <span className="text-[9px] font-medium text-grey-400 uppercase">
                    {dayOfWeek}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-semibold leading-tight',
                      isToday
                        ? 'w-6 h-6 flex items-center justify-center rounded-full bg-accent text-white'
                        : 'text-secondary'
                    )}
                  >
                    {dayNum}
                  </span>
                </div>

                {/* Events and Tasks */}
                <div className="flex-1 py-1 px-1.5 space-y-0.5 overflow-hidden">
                  {dayItems.length === 0 ? (
                    onAddEvent ? (
                      <button
                        onClick={() => onAddEvent(day)}
                        className="w-full h-full min-h-[24px] flex items-center justify-center text-[10px] text-grey-300 hover:text-grey-400 transition-colors"
                      >
                        —
                      </button>
                    ) : (
                      <div className="w-full h-full min-h-[24px] flex items-center justify-center text-[10px] text-grey-300">
                        —
                      </div>
                    )
                  ) : (
                    dayItems.slice(0, 2).map((item) => {
                      if (item.type === 'event') {
                        const color = getEventColor(item.data.color);
                        const canEdit = canEditEvent?.(item.data) ?? false;

                        // Render clickable for users with edit permission
                        if (canEdit) {
                          return (
                            <button
                              key={`event-${item.data.id}`}
                              onClick={() => onEventClick?.(item.data)}
                              className={cn(
                                'w-full text-left px-1.5 py-0.5 rounded text-[10px] truncate',
                                'border-l-2 transition-colors hover:opacity-80',
                                color.bg,
                                color.border,
                                color.text
                              )}
                              title={item.data.title}
                            >
                              {item.data.title}
                            </button>
                          );
                        }

                        // Non-clickable for users without permission
                        return (
                          <div
                            key={`event-${item.data.id}`}
                            className={cn(
                              'w-full text-left px-1.5 py-0.5 rounded text-[10px] truncate',
                              'border-l-2',
                              color.bg,
                              color.border,
                              color.text
                            )}
                            title={item.data.title}
                          >
                            {item.data.title}
                          </div>
                        );
                      } else {
                        // Task
                        return (
                          <button
                            key={`task-${item.data.id}`}
                            onClick={() => onTaskClick?.(item.data)}
                            className={cn(
                              'w-full text-left px-1.5 py-0.5 rounded text-[10px] truncate',
                              'border-l-2 transition-colors hover:opacity-80 flex items-center gap-1',
                              taskColor.bg,
                              taskColor.border,
                              taskColor.text
                            )}
                            title={item.data.title}
                          >
                            <CheckSquare className="w-2.5 h-2.5 shrink-0" />
                            <span className="truncate">{item.data.title}</span>
                          </button>
                        );
                      }
                    })
                  )}
                  {dayItems.length > 2 && (
                    <button
                      onClick={() => navigate(`/projects/${projectId}/events?date=${dayKey}`)}
                      className="text-[9px] text-grey-400 hover:text-accent pl-1.5"
                    >
                      +{dayItems.length - 2} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty State */
        <div className="py-8 px-6 text-center">
          <p className="text-sm text-grey-500">No events this week</p>
        </div>
      )}

      {/* Footer: View calendar & Add Event */}
      <div className="px-6 py-4 border-t border-grey-100 space-y-3">
        {/* View calendar link - always visible */}
        <button
          onClick={() => navigate(`/projects/${projectId}/events`)}
          className="text-sm text-accent hover:underline font-medium"
        >
          View calendar →
        </button>

        {/* Add event button */}
        {onAddEvent && (
          <button
            onClick={() => onAddEvent()}
            className="flex items-center gap-2 text-sm text-grey-500 hover:text-accent transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add an event
          </button>
        )}
      </div>
    </div>
  );
}
